import { clearScreenState } from "./screenState.js";
// URLs contain only menu/detail identifiers, never names, filters or personal fields.
export const MENU_VIEWS = Object.freeze([
  "calendar",
  "students",
  "payments-management",
  "payments-points",
  "courses",
  "timetable",
  "examPeriods",
  "archive",
  "academy",
]);
const key = "academyMenu";
const detailKinds = {
  students: ["student"],
  examPeriods: ["examFolder"],
  archive: ["archive"],
  courses: ["course"],
  academy: ["course"],
  "payments-management": ["payment"],
};
const validId = (value) =>
  typeof value === "string" && /^[a-zA-Z0-9_-]{1,128}$/.test(value);
export function readMenu(location) {
  const v = new URL(location.href).searchParams.get("view");
  return MENU_VIEWS.includes(v) ? v : "calendar";
}
export function readRoute(location) {
  const p = new URL(location.href).searchParams,
    view = readMenu(location),
    kind = p.get("detail"),
    id = p.get("record"),
    month = p.get("billingMonth");
  if (!detailKinds[view]?.includes(kind) || !validId(id)) return { view };
  if (kind === "payment" && !/^\d{4}-(0[1-9]|1[0-2])$/.test(month || "")) {
    return { view };
  }
  const panel = p.get("panel"), entry = p.get("entry");
  const panels = { student: ["score", "enrollment"], examFolder: ["examDate"] };
  const child = panels[kind]?.includes(panel) && validId(entry);
  if (
    panel === "examDate" && child &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(entry) ||
      Number.isNaN(Date.parse(entry + "T00:00:00Z")) ||
      new Date(entry + "T00:00:00Z").toISOString().slice(0, 10) !== entry)
  ) return { view, kind, id };
  return {
    view,
    kind,
    id,
    ...(kind === "payment" ? { month } : {}),
    ...(child ? { panel, entry } : {}),
  };
}
const routeKey = (route) => JSON.stringify(route);
function routeUrl(location, route) {
  const url = new URL(location.href);
  for (
    const p of ["view", "detail", "record", "billingMonth", "panel", "entry"]
  ) url.searchParams.delete(p);
  if (route.view !== "calendar") url.searchParams.set("view", route.view);
  if (route.kind) {
    url.searchParams.set("detail", route.kind);
    url.searchParams.set("record", route.id);
    if (route.month) url.searchParams.set("billingMonth", route.month);
  }
  if (route.panel) {
    url.searchParams.set("panel", route.panel);
    url.searchParams.set("entry", route.entry);
  }
  return url;
}
export function resetMenu(browser = globalThis.window) {
  clearScreenState(browser);
  browser.history.replaceState(
    browser.history.state,
    "",
    routeUrl(browser.location, { view: "calendar" }),
  );
}
export function createMenuNavigation(
  browser,
  { onChange, blocked = () => false },
) {
  const previous = browser.history.state?.[key];
  const resume = previous && typeof previous.session === "string" &&
    /^[a-f0-9-]{36}$/.test(previous.session) &&
    Number.isSafeInteger(previous.index) && previous.index >= 0 &&
    previous.index < 1000000;
  const session = resume ? previous.session : browser.crypto.randomUUID();
  let index = resume ? previous.index : 0,
    route = readRoute(browser.location),
    restoring = false;
  const state = (parent) => ({
    ...browser.history.state,
    [key]: { session, index, parent },
  });
  const publish = () => onChange(route.view, route);
  browser.history.replaceState(
    state(resume ? previous.parent : undefined),
    "",
    routeUrl(browser.location, route),
  );
  function pop(event) {
    if (restoring) {
      restoring = false;
      return;
    }
    const target = event.state?.[key];
    if (blocked()) {
      if (target?.session === session && target.index !== index) {
        restoring = true;
        browser.history.go(index - target.index);
      } else {
        browser.history.replaceState(
          state(),
          "",
          routeUrl(browser.location, route),
        );
      }
      return;
    }
    index = target?.session === session ? target.index : 0;
    route = readRoute(browser.location);
    browser.history.replaceState(
      state(target?.parent),
      "",
      routeUrl(browser.location, route),
    );
    publish();
  }
  function apply(next, replace = false) {
    if (restoring || blocked() || routeKey(next) === routeKey(route)) return;
    const parent = routeKey(route);
    route = next;
    if (!replace) index++;
    browser.history[replace ? "replaceState" : "pushState"](
      state(replace ? undefined : parent),
      "",
      routeUrl(browser.location, route),
    );
    publish();
  }
  browser.addEventListener("popstate", pop);
  return {
    navigate(view) {
      if (MENU_VIEWS.includes(view)) apply({ view });
    },
    open(kind, id, month) {
      if (!detailKinds[route.view]?.includes(kind) || !validId(id)) return;
      const next = readRoute({
        href: routeUrl(browser.location, { view: route.view, kind, id, month })
          .href,
      });
      if (next.kind) apply(next);
    },
    openPanel(panel, entry) {
      const next = readRoute({
        href: routeUrl(browser.location, { ...route, panel, entry }).href,
      });
      if (next.panel) apply(next);
    },
    close() {
      if (!route.kind || restoring || blocked()) return;
      const parent = route.panel
          ? { view: route.view, kind: route.kind, id: route.id }
          : { view: route.view },
        entry = browser.history.state?.[key];
      if (
        entry?.session === session && index > 0 &&
        entry.parent === routeKey(parent)
      ) browser.history.back();
      else apply(parent, true);
    },
    dispose() {
      browser.removeEventListener("popstate", pop);
    },
  };
}
